---
title: Configuration
description: How a setting travels from a form field to the GPU machine.
---

## One file, every knob

Everything tunable about a store's pipeline lives in one file on its GPU
machine: `store.yml`. Stream quality, camera counts and good thresholds are
unknown until a real site, so nothing tunable is ever hardcoded. Every knob
has a tested default and is registered in the settings UI with validation.

The settings pages in this app are views onto that file. Each section shows
the config key it edits, and every field's info icon explains what the knob
does, its default, and when to change it.

## The push path

1. You change a field and press save.
2. The gateway validates the change against a typed allowlist. Unknown keys
   and wrong types are refused here, not discovered at 2 a.m. on the box.
3. The gateway stores the new configuration in Postgres, so the app always
   knows what it last sent.
4. The gateway pushes the configuration to the store's control plane, which
   writes `store.yml`, `zones.yml` and the per-camera files.
5. The next run picks it up. Environment variables on the machine override
   pushed values at run time, so a box can be pinned locally when needed.

A few fields, like the GPU machine's own address, exist only in the gateway's
database and are never pushed. The machine does not need to know how the app
reaches it.

## Things that are never configured

- **Camera count.** It is the number of videos in the night's session.
  Batch size, tile layout and the tracker's peer mesh all derive from it.
- **Detector input size.** Each model size was exported for exact
  dimensions. A mismatch does not crash; it silently returns garbage. So the
  UI derives it from the model size and refuses edits.
- **Secrets.** Configuration carries the names of environment variables,
  never values. The committed config is safe to read anywhere.

## Zones and calibration

Counting lines and zones are drawn in this app on the floor plan and stored
in meters, in the same world coordinates the calibration produced. Pixels
are never the unit, because every camera sees the same floor differently and
cameras get bumped. When a camera is moved, recalibrate it; its pixels moved,
but the floor plan did not.
